import { sendWidgetEvent } from './api';
import { WIDGET_NAME } from '@bikdotai/bik-models/dm';

export const generateRotationValues = (
  spikesCount: number,
  templateName: WIDGET_NAME,
) => {
  return templateName === WIDGET_NAME.STW1
    ? getRotationValuesSTW1(spikesCount)
    : document.body.clientWidth < 450
      ? getRotationValuesMobileSTW2(spikesCount)
      : getRotationValuesDesktopSTW2(spikesCount);
};

const getRotationValuesSTW1 = (spikesCount: number) => {
  const rotationValuesTemp = [];
  const segmentAngle = 360 / spikesCount;
  const firstHalfSegement = {
    minDegree: 0,
    maxDegree: segmentAngle / 2,
    value: 1,
  };
  rotationValuesTemp.push(firstHalfSegement);
  for (let i = spikesCount; i >= 2; i--) {
    const lastValue = rotationValuesTemp[rotationValuesTemp.length - 1];
    const currentSegmentValue = {
      minDegree: lastValue.maxDegree + 1,
      maxDegree: lastValue.maxDegree + segmentAngle,
      value: i,
    };
    rotationValuesTemp.push(currentSegmentValue);
  }
  const lastValue = rotationValuesTemp[rotationValuesTemp.length - 1];
  const secondHalfSegement = {
    minDegree: lastValue.maxDegree + 1,
    maxDegree: lastValue.maxDegree + segmentAngle / 2,
    value: 1,
  };
  rotationValuesTemp.push(secondHalfSegement);
  return rotationValuesTemp;
};

const getStartSectorDesktop = (spikesCount: number) => {
  const halfSectorsCount = spikesCount / 2;
  const sectorsInBtw = Math.ceil((halfSectorsCount - 1) / 2);
  const startSector = 1 + sectorsInBtw;
  return startSector;
};

const getRotationValuesDesktopSTW2 = (spikesCount: number) => {
  let rotationValuesTemp = [];
  let segmentAngle = 360 / spikesCount;
  const startSector = getStartSectorDesktop(spikesCount);
  const startingPoint =
    spikesCount % 4 === 0
      ? {
          minDegree: 0,
          maxDegree: segmentAngle / 2,
          value: startSector,
        }
      : {
          minDegree: 0,
          maxDegree: segmentAngle,
          value: startSector,
        };
  rotationValuesTemp.push(startingPoint);
  let lastPoint = startingPoint;
  for (let i = startSector - 1; i >= 1; i--) {
    let currentSector = {
      minDegree: lastPoint.maxDegree + 1,
      maxDegree: lastPoint.maxDegree + segmentAngle,
      value: i,
    };
    rotationValuesTemp.push(currentSector);
    lastPoint = currentSector;
  }
  let lastValue = rotationValuesTemp[rotationValuesTemp.length - 1];
  let i;
  for (i = spikesCount; i > startSector; i--) {
    const currentSegmentValue = {
      minDegree: lastValue.maxDegree + 1,
      maxDegree: lastValue.maxDegree + segmentAngle,
      value: i,
    };
    rotationValuesTemp.push(currentSegmentValue);
    lastValue = currentSegmentValue;
  }
  if (spikesCount % 4 === 0) {
    let secondHalfSegement = {
      minDegree: lastValue.maxDegree + 1,
      maxDegree: lastValue.maxDegree + segmentAngle / 2,
      value: i,
    };
    rotationValuesTemp.push(secondHalfSegement);
  }
  return rotationValuesTemp;
};

const getRotationValuesMobileSTW2 = (spikesCount: number) => {
  let rotationValuesTemp = [];
  let segmentAngle = 360 / spikesCount;
  let firstHalfSegement = {
    minDegree: 0,
    maxDegree: segmentAngle / 2,
    value: 1,
  };
  rotationValuesTemp.push(firstHalfSegement);
  for (let i = spikesCount; i >= 2; i--) {
    const lastValue = rotationValuesTemp[rotationValuesTemp.length - 1];
    const currentSegmentValue = {
      minDegree: lastValue.maxDegree + 1,
      maxDegree: lastValue.maxDegree + segmentAngle,
      value: i,
    };
    rotationValuesTemp.push(currentSegmentValue);
  }
  const lastValue = rotationValuesTemp[rotationValuesTemp.length - 1];
  let secondHalfSegement = {
    minDegree: lastValue.maxDegree + 1,
    maxDegree: lastValue.maxDegree + segmentAngle / 2,
    value: 1,
  };
  rotationValuesTemp.push(secondHalfSegement);
  return rotationValuesTemp;
};

export const colorLuminance = (hex, lum) => {
  hex = String(hex).replace(/[^0-9a-f]/gi, '');
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  lum = lum || 0;
  let rgb = '#',
    c,
    i;
  for (i = 0; i < 3; i++) {
    c = parseInt(hex.substr(i * 2, 2), 16);
    c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
    rgb += ('00' + c).substr(c.length);
  }
  return rgb;
};

export const getRgbColor = (color: string) => {
  // Convert the color to an RGB color using a temporary DOM element
  const tempElem = document.createElement('div');
  tempElem.style.color = color;
  document.body.appendChild(tempElem);
  const rgbColor = window.getComputedStyle(tempElem).color;
  document.body.removeChild(tempElem);

  // Parse the RGB values from the color string
  const match = rgbColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  return match
    ? { r: +match[1], g: +match[2], b: +match[3] }
    : { r: 0, g: 0, b: 0 };
};

export const getContrastColor = (bgColor: string) => {
  // Convert the background color to an RGB color
  const color = getRgbColor(bgColor);

  // Calculate the relative luminance of the color using the sRGB color space formula
  const luminance = 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
  // Choose white or black as the text color based on the luminance value
  if (luminance > 100) {
    return '#212121';
  } else {
    return '#FFFFFF';
  }
};

export const getCookie = name => {
  const cookies = document.cookie
    .split(';')
    .map(cookie => cookie.split('='))
    .reduce(
      (accumulator, [key, value]) => ({
        ...accumulator,
        [key.trim()]: decodeURIComponent(value),
      }),
      {},
    );
  return cookies[name];
};

export const setCookie = (cname, cvalue, exdays) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = 'expires=' + d.toUTCString();
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
};
export const cookieProcessor = (
  cookieName,
  cookieValue,
  eventName,
  exday,
  widgetId,
  env,
) => {
  setCookie(cookieName, cookieValue, exday);
  sendWidgetEvent(eventName, widgetId, env);
};
export const cookieClickHandler = (
  cookieName,
  cookieValue,
  event,
  exDay,
  widgetId,
  env,
) => {
  if (getCookie(cookieName) !== cookieValue) {
    cookieProcessor(cookieName, cookieValue, event, exDay, widgetId, env);
  }
};

export const getScrollPercent = () => {
  var winHeight = window.innerHeight;
  var docHeight = document.body.clientHeight;
  var scrollTop = window.scrollY;
  var trackLength = docHeight - winHeight;
  var pctScrolled = Math.floor((scrollTop / trackLength) * 100);
  return pctScrolled;
};
export const segmentHeightMap = {
  'Spin the wheel popup 2': {
    previewMobile: 108,
    previewDesktop: 88,
    defaultHeightDesktop: 180,
    defaultHeightMobile: 195,
  },
  'Spin the wheel popup': {
    previewMobile: 87,
    previewDesktop: 62,
    defaultHeightDesktop: 180,
    defaultHeightMobile: 148,
  },
};
export const updateSegmentWidth = (
  spikesCount: number,
  preview: boolean,
  isMobile: boolean,
  templateName: WIDGET_NAME,
) => {
  let segmentHeight = null;
  if (preview) {
    if (isMobile) {
      segmentHeight = segmentHeightMap[templateName].previewMobile;
    } else {
      segmentHeight = segmentHeightMap[templateName].previewDesktop;
    }
  } else {
    if (window.screen.width > 768) {
      segmentHeight = segmentHeightMap[templateName].defaultHeightDesktop;
    } else {
      segmentHeight = segmentHeightMap[templateName].defaultHeightMobile;
    }
  }
  const width =
    Math.tan(((360 / (spikesCount * 2)) * Math.PI) / 180) * segmentHeight * 2;
  return width;
};

export const getSegmentDetails = (
  spikesCount: number,
  segmentColor1: string,
  segmentColor2: string,
  discountCodeTitle,
) => {
  const segmentDegree = 360 / spikesCount;
  let wheelDetails = [];
  for (let i = 0; i < spikesCount; i++) {
    wheelDetails = [
      ...wheelDetails,
      getRotationValues(i, segmentDegree, discountCodeTitle),
    ];
  }
  for (let i = 0; i < spikesCount; i = i + 2) {
    wheelDetails[i] = {
      ...wheelDetails[i],
      color: segmentColor1,
    };
    wheelDetails[i + 1] = {
      ...wheelDetails[i + 1],
      color: segmentColor2,
    };
  }
  return wheelDetails;
};

export const getRotationValues = (
  idx: number,
  segmentDegree: number,
  discountCodeTitle,
) => {
  let obj = {};
  switch (idx) {
    case 0:
      return (obj = {
        class: 'one',
        text:
          discountCodeTitle && discountCodeTitle[1]
            ? discountCodeTitle[1]
            : 'Flat 10% off',
        rotationValue: segmentDegree * idx,
      });

    case 1:
      return (obj = {
        class: 'two',
        text:
          discountCodeTitle && discountCodeTitle[2]
            ? discountCodeTitle[2]
            : 'Flat 20% off',
        rotationValue: segmentDegree * idx,
      });

    case 2:
      return (obj = {
        class: 'three',
        text:
          discountCodeTitle && discountCodeTitle[3]
            ? discountCodeTitle[3]
            : 'Flat 30% off',
        rotationValue: segmentDegree * idx,
      });

    case 3:
      return (obj = {
        class: 'four',
        text:
          discountCodeTitle && discountCodeTitle[4]
            ? discountCodeTitle[4]
            : 'Flat 40% off',
        rotationValue: segmentDegree * idx,
      });
    case 4:
      return (obj = {
        class: 'five',
        text:
          discountCodeTitle && discountCodeTitle[5]
            ? discountCodeTitle[5]
            : 'Flat 50% off',
        rotationValue: segmentDegree * idx,
      });

    case 5:
      return (obj = {
        class: 'six',
        text:
          discountCodeTitle && discountCodeTitle[6]
            ? discountCodeTitle[6]
            : 'Flat 60% off',
        rotationValue: segmentDegree * idx,
      });

    case 6:
      return (obj = {
        class: 'seven',
        text:
          discountCodeTitle && discountCodeTitle[7]
            ? discountCodeTitle[7]
            : 'Flat 70% off',
        rotationValue: segmentDegree * idx,
      });
    case 7:
      return (obj = {
        class: 'eight',
        text:
          discountCodeTitle && discountCodeTitle[8]
            ? discountCodeTitle[8]
            : 'Flat 80% off',
        rotationValue: segmentDegree * idx,
      });
    case 8:
      return (obj = {
        class: 'nine',
        text:
          discountCodeTitle && discountCodeTitle[9]
            ? discountCodeTitle[9]
            : 'Flat 90% off',
        rotationValue: segmentDegree * idx,
      });
    case 9:
      return (obj = {
        class: 'ten',
        text:
          discountCodeTitle && discountCodeTitle[10]
            ? discountCodeTitle[10]
            : 'Flat 100% off',
        rotationValue: segmentDegree * idx,
      });
  }
};

export const addDiscountText = (discountCodeTitle, segmentDetails) => {
  let wheelDetails = [...segmentDetails];
  wheelDetails.map((desc, index) => {
    const respectiveDiscountCodeDataKey = Object.keys(discountCodeTitle).filter(
      key => key === (index + 1).toString(),
    );
    if (respectiveDiscountCodeDataKey) {
      wheelDetails[index] = {
        ...wheelDetails[index],
        text: discountCodeTitle[respectiveDiscountCodeDataKey[0]],
      };
    }
  });
  return wheelDetails;
};

export const CountriesData = [
  { name: 'United Kingdom', dial: '+44', code: 'GB', code3: 'GBR' },
  { name: 'United States', dial: '+1', code: 'US', code3: 'USA' },
  { name: 'Germany', dial: '+49', code: 'DE', code3: 'DEU' },
  { name: 'Australia', dial: '+61', code: 'AU', code3: 'AUS' },
  { name: 'Singapore', dial: '+65', code: 'SG', code3: 'SGP' },
  { name: 'United Arab Emirates', dial: '+971', code: 'AE', code3: 'ARE' },
  { name: 'France', dial: '+33', code: 'FR', code3: 'FRA' },
  { name: 'India', dial: '+91', code: 'IN', code3: 'IND' },
  { name: 'Italy', dial: '+39', code: 'IT', code3: 'ITA' },
  { name: 'Japan', dial: '+81', code: 'JP', code3: 'JPN' },
  { name: 'Afghanistan', dial: '+93', code: 'AF', code3: 'AFG' },
  { name: 'Albania', dial: '+355', code: 'AL', code3: 'ALB' },
  { name: 'Algeria', dial: '+213', code: 'DZ', code3: 'DZA' },
  { name: 'Andorra', dial: '+376', code: 'AD', code3: 'AND' },
  { name: 'Angola', dial: '+244', code: 'AO', code3: 'AGO' },
  { name: 'Anguilla', dial: '+1264', code: 'AI', code3: 'AIA' },
  { name: 'Antarctica', dial: '+672', code: 'AQ', code3: 'ATA' },
  { name: 'Antigua and Barbuda', dial: '+1268', code: 'AG', code3: 'ATG' },
  { name: 'Argentina', dial: '+54', code: 'AR', code3: 'ARG' },
  { name: 'Armenia', dial: '+374', code: 'AM', code3: 'ARM' },
  { name: 'Aruba', dial: '+297', code: 'AW', code3: 'ABW' },
  { name: 'Austria', dial: '+43', code: 'AT', code3: 'AUT' },
  { name: 'Azerbaijan', dial: '+994', code: 'AZ', code3: 'AZE' },
  { name: 'Bahamas', dial: '+1242', code: 'BS', code3: 'BHS' },
  { name: 'Bahrain', dial: '+973', code: 'BH', code3: 'BHR' },
  { name: 'Bangladesh', dial: '+880', code: 'BD', code3: 'BGD' },
  { name: 'Barbados', dial: '+1246', code: 'BB', code3: 'BRB' },
  { name: 'Belarus', dial: '+375', code: 'BY', code3: 'BLR' },
  { name: 'Belgium', dial: '+32', code: 'BE', code3: 'BEL' },
  { name: 'Belize', dial: '+501', code: 'BZ', code3: 'BLZ' },
  { name: 'Benin', dial: '+229', code: 'BJ', code3: 'BEN' },
  { name: 'Bermuda', dial: '+1441', code: 'BM', code3: 'BMU' },
  { name: 'Bhutan', dial: '+975', code: 'BT', code3: 'BTN' },
  { name: 'Bosnia and Herzegovina', dial: '+387', code: 'BA', code3: 'BIH' },
  { name: 'Botswana', dial: '+267', code: 'BW', code3: 'BWA' },
  { name: 'Brazil', dial: '+55', code: 'BR', code3: 'BRA' },
  {
    name: 'British Indian Ocean Territory',
    dial: '+246',
    code: 'IO',
    code3: 'IOT',
  },
  { name: 'Bulgaria', dial: '+359', code: 'BG', code3: 'BGR' },
  { name: 'Burkina Faso', dial: '+226', code: 'BF', code3: 'BFA' },
  { name: 'Burundi', dial: '+257', code: 'BI', code3: 'BDI' },
  { name: 'Cambodia', dial: '+855', code: 'KH', code3: 'KHM' },
  { name: 'Cameroon', dial: '+237', code: 'CM', code3: 'CMR' },
  { name: 'Canada', dial: '+1', code: 'CA', code3: 'CAN' },
  { name: 'Cape Verde', dial: '+238', code: 'CV', code3: 'CPV' },
  { name: 'Central African Republic', dial: '+236', code: 'CF', code3: 'CAF' },
  { name: 'Chad', dial: '+235', code: 'TD', code3: 'TCD' },
  { name: 'Chile', dial: '+56', code: 'CL', code3: 'CHL' },
  { name: 'China', dial: '+86', code: 'CN', code3: 'CHN' },
  { name: 'Christmas Island', dial: '+61', code: 'CX', code3: 'CXR' },
  { name: 'Colombia', dial: '+57', code: 'CO', code3: 'COL' },
  { name: 'Comoros', dial: '+269', code: 'KM', code3: 'COM' },
  { name: 'Cook Islands', dial: '+682', code: 'CK', code3: 'COK' },
  { name: 'Costa Rica', dial: '+506', code: 'CR', code3: 'CRI' },
  { name: 'Croatia', dial: '+385', code: 'HR', code3: 'HRV' },
  { name: 'Cuba', dial: '+53', code: 'CU', code3: 'CUB' },
  { name: 'Cyprus', dial: '+357', code: 'CY', code3: 'CYP' },
  { name: 'Czech Republic', dial: '+420', code: 'CZ', code3: 'CZE' },
  { name: 'Denmark', dial: '+45', code: 'DK', code3: 'DNK' },
  { name: 'Djibouti', dial: '+253', code: 'DJ', code3: 'DJI' },
  { name: 'Dominica', dial: '+1767', code: 'DM', code3: 'DMA' },
  { name: 'Ecuador', dial: '+593', code: 'EC', code3: 'ECU' },
  { name: 'Egypt', dial: '+20', code: 'EG', code3: 'EGY' },
  { name: 'El Salvador', dial: '+503', code: 'SV', code3: 'SLV' },
  { name: 'Equatorial Guinea', dial: '+240', code: 'GQ', code3: 'GNQ' },
  { name: 'Eritrea', dial: '+291', code: 'ER', code3: 'ERI' },
  { name: 'Estonia', dial: '+372', code: 'EE', code3: 'EST' },
  { name: 'Ethiopia', dial: '+251', code: 'ET', code3: 'ETH' },
  { name: 'Faroe Islands', dial: '+298', code: 'FO', code3: 'FRO' },
  { name: 'Fiji', dial: '+679', code: 'FJ', code3: 'FJI' },
  { name: 'Finland', dial: '+358', code: 'FI', code3: 'FIN' },
  { name: 'French Polynesia', dial: '+689', code: 'PF', code3: 'PYF' },
  { name: 'Gabon', dial: '+241', code: 'GA', code3: 'GAB' },
  { name: 'Gambia', dial: '+220', code: 'GM', code3: 'GMB' },
  { name: 'Georgia', dial: '+995', code: 'GE', code3: 'GEO' },
  { name: 'Ghana', dial: '+233', code: 'GH', code3: 'GHA' },
  { name: 'Gibraltar', dial: '+350', code: 'GI', code3: 'GIB' },
  { name: 'Greece', dial: '+30', code: 'GR', code3: 'GRC' },
  { name: 'Greenland', dial: '+299', code: 'GL', code3: 'GRL' },
  { name: 'Grenada', dial: '+1473', code: 'GD', code3: 'GRD' },
  { name: 'Guam', dial: '+1671', code: 'GU', code3: 'GUM' },
  { name: 'Guatemala', dial: '+502', code: 'GT', code3: 'GTM' },
  { name: 'Guinea', dial: '+224', code: 'GN', code3: 'GIN' },
  { name: 'Guinea-Bissau', dial: '+245', code: 'GW', code3: 'GNB' },
  { name: 'Haiti', dial: '+509', code: 'HT', code3: 'HTI' },
  { name: 'Honduras', dial: '+504', code: 'HN', code3: 'HND' },
  { name: 'Hong Kong', dial: '+852', code: 'HK', code3: 'HKG' },
  { name: 'Hungary', dial: '+36', code: 'HU', code3: 'HUN' },
  { name: 'Iceland', dial: '+354', code: 'IS', code3: 'ISL' },
  { name: 'Indonesia', dial: '+62', code: 'ID', code3: 'IDN' },
  { name: 'Iraq', dial: '+964', code: 'IQ', code3: 'IRQ' },
  { name: 'Ireland', dial: '+353', code: 'IE', code3: 'IRL' },
  { name: 'Israel', dial: '+972', code: 'IL', code3: 'ISR' },
  { name: 'Jamaica', dial: '+1876', code: 'JM', code3: 'JAM' },
  { name: 'Jordan', dial: '+962', code: 'JO', code3: 'JOR' },
  { name: 'Kenya', dial: '+254', code: 'KE', code3: 'KEN' },
  { name: 'Kiribati', dial: '+686', code: 'KI', code3: 'KIR' },
  { name: 'Kuwait', dial: '+965', code: 'KW', code3: 'KWT' },
  { name: 'Kyrgyzstan', dial: '+996', code: 'KG', code3: 'KGZ' },
  { name: 'Laos', dial: '+856', code: 'LA', code3: 'LAO' },
  { name: 'Latvia', dial: '+371', code: 'LV', code3: 'LVA' },
  { name: 'Lebanon', dial: '+961', code: 'LB', code3: 'LBN' },
  { name: 'Lesotho', dial: '+266', code: 'LS', code3: 'LSO' },
  { name: 'Liberia', dial: '+231', code: 'LR', code3: 'LBR' },
  { name: 'Liechtenstein', dial: '+423', code: 'LI', code3: 'LIE' },
  { name: 'Lithuania', dial: '+370', code: 'LT', code3: 'LTU' },
  { name: 'Luxembourg', dial: '+352', code: 'LU', code3: 'LUX' },
  { name: 'Macao', dial: '+853', code: 'MO', code3: 'MAC' },
  { name: 'Macedonia', dial: '+389', code: 'MK', code3: 'MKD' },
  { name: 'Madagascar', dial: '+261', code: 'MG', code3: 'MDG' },
  { name: 'Malawi', dial: '+265', code: 'MW', code3: 'MWI' },
  { name: 'Malaysia', dial: '+60', code: 'MY', code3: 'MYS' },
  { name: 'Maldives', dial: '+960', code: 'MV', code3: 'MDV' },
  { name: 'Mali', dial: '+223', code: 'ML', code3: 'MLI' },
  { name: 'Malta', dial: '+356', code: 'MT', code3: 'MLT' },
  { name: 'Marshall Islands', dial: '+692', code: 'MH', code3: 'MHL' },
  { name: 'Mauritania', dial: '+222', code: 'MR', code3: 'MRT' },
  { name: 'Mauritius', dial: '+230', code: 'MU', code3: 'MUS' },
  { name: 'Mayotte', dial: '+262', code: 'YT', code3: 'MYT' },
  { name: 'Mexico', dial: '+52', code: 'MX', code3: 'MEX' },
  { name: 'Moldova', dial: '+373', code: 'MD', code3: 'MDA' },
  { name: 'Monaco', dial: '+377', code: 'MC', code3: 'MCO' },
  { name: 'Mongolia', dial: '+976', code: 'MN', code3: 'MNG' },
  { name: 'Montenegro', dial: '+382', code: 'ME', code3: 'MNE' },
  { name: 'Montserrat', dial: '+1664', code: 'MS', code3: 'MSR' },
  { name: 'Morocco', dial: '+212', code: 'MA', code3: 'MAR' },
  { name: 'Mozambique', dial: '+258', code: 'MZ', code3: 'MOZ' },
  { name: 'Myanmar', dial: '+95', code: 'MM', code3: 'MMR' },
  { name: 'Namibia', dial: '+264', code: 'NA', code3: 'NAM' },
  { name: 'Nauru', dial: '+674', code: 'NR', code3: 'NRU' },
  { name: 'Nepal', dial: '+977', code: 'NP', code3: 'NPL' },
  { name: 'Netherlands', dial: '+31', code: 'NL', code3: 'NLD' },
  { name: 'Netherlands Antilles', dial: '+599', code: 'AN', code3: 'ANT' },
  { name: 'New Caledonia', dial: '+687', code: 'NC', code3: 'NCL' },
  { name: 'New Zealand', dial: '+64', code: 'NZ', code3: 'NZL' },
  { name: 'Nicaragua', dial: '+505', code: 'NI', code3: 'NIC' },
  { name: 'Niger', dial: '+227', code: 'NE', code3: 'NER' },
  { name: 'Nigeria', dial: '+234', code: 'NG', code3: 'NGA' },
  { name: 'Niue', dial: '+683', code: 'NU', code3: 'NIU' },
  { name: 'Norfolk Island', dial: '+672', code: 'NF', code3: 'NFK' },
  { name: 'Northern Mariana Islands', dial: '+1670', code: 'MP', code3: 'MNP' },
  { name: 'Norway', dial: '+47', code: 'NO', code3: 'NOR' },
  { name: 'Oman', dial: '+968', code: 'OM', code3: 'OMN' },
  { name: 'Pakistan', dial: '+92', code: 'PK', code3: 'PAK' },
  { name: 'Palau', dial: '+680', code: 'PW', code3: 'PLW' },
  { name: 'Panama', dial: '+507', code: 'PA', code3: 'PAN' },
  { name: 'Papua New Guinea', dial: '+675', code: 'PG', code3: 'PNG' },
  { name: 'Paraguay', dial: '+595', code: 'PY', code3: 'PRY' },
  { name: 'Peru', dial: '+51', code: 'PE', code3: 'PER' },
  { name: 'Philippines', dial: '+63', code: 'PH', code3: 'PHL' },
  { name: 'Poland', dial: '+48', code: 'PL', code3: 'POL' },
  { name: 'Portugal', dial: '+351', code: 'PT', code3: 'PRT' },
  { name: 'Romania', dial: '+40', code: 'RO', code3: 'ROU' },
  { name: 'Russia', dial: '+7', code: 'RU', code3: 'RUS' },
  { name: 'Rwanda', dial: '+250', code: 'RW', code3: 'RWA' },
  { name: 'Saint Kitts and Nevis', dial: '+1869', code: 'KN', code3: 'KNA' },
  { name: 'Saint Lucia', dial: '+1758', code: 'LC', code3: 'LCA' },
  { name: 'Saint Pierre and Miquelon', dial: '+508', code: 'PM', code3: 'SPM' },
  {
    name: 'Saint Vincent and the Grenadines',
    dial: '+1784',
    code: 'VC',
    code3: 'VCT',
  },
  { name: 'Samoa', dial: '+685', code: 'WS', code3: 'WSM' },
  { name: 'San Marino', dial: '+378', code: 'SM', code3: 'SMR' },
  { name: 'Sao Tome and Principe', dial: '+239', code: 'ST', code3: 'STP' },
  { name: 'Saudi Arabia', dial: '+966', code: 'SA', code3: 'SAU' },
  { name: 'Senegal', dial: '+221', code: 'SN', code3: 'SEN' },
  { name: 'Serbia', dial: '+381', code: 'RS', code3: 'SRB' },
  { name: 'Seychelles', dial: '+248', code: 'SC', code3: 'SYC' },
  { name: 'Sierra Leone', dial: '+232', code: 'SL', code3: 'SLE' },
  { name: 'Slovakia', dial: '+421', code: 'SK', code3: 'SVK' },
  { name: 'Slovenia', dial: '+386', code: 'SI', code3: 'SVN' },
  { name: 'Solomon Islands', dial: '+677', code: 'SB', code3: 'SLB' },
  { name: 'Somalia', dial: '+252', code: 'SO', code3: 'SOM' },
  { name: 'South Africa', dial: '+27', code: 'ZA', code3: 'ZAF' },
  { name: 'South Sudan', dial: '+211', code: 'SS', code3: 'SSD' },
  {
    name: 'South Georgia and the South Sandwich Islands',
    dial: '+500',
    code: 'GS',
    code3: 'SGS',
  },
  { name: 'Spain', dial: '+34', code: 'ES', code3: 'ESP' },
  { name: 'Sri Lanka', dial: '+94', code: 'LK', code3: 'LKA' },
  { name: 'Sudan', dial: '+249', code: 'SD', code3: 'SDN' },
  { name: 'Suriname', dial: '+597', code: 'SR', code3: 'SUR' },
  { name: 'Svalbard and Jan Mayen', dial: '+47', code: 'SJ', code3: 'SJM' },
  { name: 'Sweden', dial: '+46', code: 'SE', code3: 'SWE' },
  { name: 'Switzerland', dial: '+41', code: 'CH', code3: 'CHE' },
  { name: 'Syrian Arab Republic', dial: '+963', code: 'SY', code3: 'SYR' },
  { name: 'Taiwan', dial: '+886', code: 'TW', code3: 'TWN' },
  { name: 'Tajikistan', dial: '+992', code: 'TJ', code3: 'TJK' },
  { name: 'Thailand', dial: '+66', code: 'TH', code3: 'THA' },
  { name: 'Timor-Leste', dial: '+670', code: 'TL', code3: 'TLS' },
  { name: 'Togo', dial: '+228', code: 'TG', code3: 'TGO' },
  { name: 'Tokelau', dial: '+690', code: 'TK', code3: 'TKL' },
  { name: 'Tonga', dial: '+676', code: 'TO', code3: 'TON' },
  { name: 'Trinidad and Tobago', dial: '+1868', code: 'TT', code3: 'TTO' },
  { name: 'Tunisia', dial: '+216', code: 'TN', code3: 'TUN' },
  { name: 'Turkey', dial: '+90', code: 'TR', code3: 'TUR' },
  { name: 'Turkmenistan', dial: '+993', code: 'TM', code3: 'TKM' },
  { name: 'Turks and Caicos Islands', dial: '+1649', code: 'TC', code3: 'TCA' },
  { name: 'Tuvalu', dial: '+688', code: 'TV', code3: 'TUV' },
  { name: 'Uganda', dial: '+256', code: 'UG', code3: 'UGA' },
  { name: 'Ukraine', dial: '+380', code: 'UA', code3: 'UKR' },
  { name: 'Uruguay', dial: '+598', code: 'UY', code3: 'URY' },
  { name: 'Uzbekistan', dial: '+998', code: 'UZ', code3: 'UZB' },
  { name: 'Vanuatu', dial: '+678', code: 'VU', code3: 'VUT' },
  { name: 'Vietnam', dial: '+84', code: 'VN', code3: 'VNM' },
  { name: 'Virgin Islands, British', dial: '+1284', code: 'VG', code3: 'VGB' },
  { name: 'Virgin Islands, U.S.', dial: '+1340', code: 'VI', code3: 'VIR' },
  { name: 'Wallis and Futuna', dial: '+681', code: 'WF', code3: 'WLF' },
  { name: 'Yemen', dial: '+967', code: 'YE', code3: 'YEM' },
  { name: 'Zambia', dial: '+260', code: 'ZM', code3: 'ZMB' },
  { name: 'Zimbabwe', dial: '+263', code: 'ZW', code3: 'ZWE' },
  {
    name: 'Korea, Republic of South Korea',
    dial: '+82',
    code: 'KR',
    code3: 'KOR',
  },
  {
    name: 'AmericanSamoa',
    dial: '+1684',
    code: 'AS',
    code3: 'ASM',
  },
  {
    name: "Korea, Democratic People's Republic of Korea",
    dial: '+850',
    code: 'KP',
    code3: 'PRK',
  },
  {
    name: 'Venezuela, Bolivarian Republic of Venezuela',
    dial: '+58',
    code: 'VE',
    code3: 'VEN',
  },
  {
    name: 'Virgin Islands, British',
    dial: '+1284',
    code: 'VG',
    code3: 'VGB',
  },
];
